import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://thepoeticmethod.com';
const DEFAULT_TITLE = 'The Poetic Method | Healthcare Organizational Consulting';
const DEFAULT_DESCRIPTION = 'Strategic partnerships designed for institutional transformation. The Poetic Method integrates expressive arts and evidence-based frameworks to drive measurable outcomes in healthcare organizations.';
const OG_IMAGE = '/og-image.png';

export function useSEO({
  title,
  description = DEFAULT_DESCRIPTION,
  ogImage = OG_IMAGE,
  ogType = 'website',
  noIndex = false,
} = {}) {
  const location = useLocation();
  const currentUrl = `${SITE_URL}${location.pathname}`;
  const pageTitle = title ? `${title} | The Poetic Method` : DEFAULT_TITLE;

  useEffect(() => {
    // Update document title
    document.title = pageTitle;

    // Update or create meta tags
    const metaTags = {
      description: description,
      'og:title': pageTitle,
      'og:description': description,
      'og:type': ogType,
      'og:url': currentUrl,
      'og:image': `${SITE_URL}${ogImage}`,
      'og:site_name': 'The Poetic Method',
      'og:locale': 'en_US',
      'twitter:card': 'summary_large_image',
      'twitter:title': pageTitle,
      'twitter:description': description,
      'twitter:image': `${SITE_URL}${ogImage}`,
    };

    // AI-specific meta tags for better AI search visibility
    const aiMetaTags = {
      'ai:content-type': 'Healthcare Consulting',
      'ai:author': 'The Poetic Method',
      'ai:published-date': new Date().toISOString().split('T')[0],
      'ai:expertise': 'Healthcare Organizational Consulting, Improvement Science, Expressive Arts',
      'ai:target-audience': 'Healthcare Leaders, Hospital Executives, Healthcare Organizations',
    };

    // Update existing meta tags or create new ones
    Object.entries(metaTags).forEach(([name, content]) => {
      const isOg = name.startsWith('og:');
      const isTwitter = name.startsWith('twitter:');
      
      let selector;
      if (isOg) {
        selector = `meta[property="${name}"]`;
      } else if (isTwitter) {
        selector = `meta[name="${name}"]`;
      } else {
        selector = `meta[name="${name}"]`;
      }

      let meta = document.querySelector(selector);
      if (!meta) {
        meta = document.createElement('meta');
        if (isOg) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    });

    // Handle robots meta tag
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute('content', noIndex ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');

    // AI-specific meta tags
    Object.entries(aiMetaTags).forEach(([name, content]) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    });

    // Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', currentUrl);

    // Cleanup function
    return () => {
      // Note: We don't remove meta tags on cleanup as they should persist
      // until the next page updates them
    };
  }, [pageTitle, description, currentUrl, ogImage, ogType, noIndex]);

  return {
    title: pageTitle,
    url: currentUrl,
  };
}
